import React from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquare, Sparkles, CheckCircle2 } from 'lucide-react';

// ============================================================================
// TestGateExplainerModal — why this campaign can't go live yet.
//
// Fires on ANY attempt to activate a campaign that hasn't cleared the pre-launch
// conversation gate: clicking the locked Go live button, or a server 409
// TEST_GATE_NOT_CLEARED coming back at a stale frontend. It does NOT unlock
// anything and it does not replace the locked button — the button stays locked.
// This is purely the explanation for why, so the block is never silent.
//
// Tone: this is the last thing between a customer and their launch, so it reads
// as a reason, not a refusal.
// ============================================================================

export default function TestGateExplainerModal({ campaign, gate, onClose, onStartTesting }) {
  const required = gate?.required ?? 3;
  const done = gate?.count ?? 0;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-900/55 p-4 font-sans antialiased"
      onClick={onClose}>
      <div className="relative w-full max-w-[440px] overflow-hidden rounded-2xl bg-white shadow-[0_30px_80px_rgba(18,30,50,.35)]"
        onClick={(e) => e.stopPropagation()}>

        <button onClick={onClose} aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
          <X className="h-4 w-4" />
        </button>

        <div className="px-7 pb-7 pt-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
            <MessageSquare className="h-6 w-6" />
          </div>

          <h3 className="mt-4 text-[18px] font-bold leading-snug text-slate-800">
            Before your campaign goes live, we want to make sure your AI is ready for real leads.
          </h3>

          <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
            Take {required} test conversations using different scenarios: an interested lead, a skeptical
            one, whatever fits your market. If something feels off, hit Recalibrate and dial it in right
            there.
          </p>
          <p className="mt-2.5 text-[13.5px] leading-relaxed text-slate-600">
            This takes a few minutes and makes all the difference.
          </p>

          {/* Progress, shown only once they're underway — at 0 of 3 it's noise. */}
          {done > 0 && (
            <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5">
              <span className="flex items-center gap-1">
                {Array.from({ length: required }).map((_, i) => (
                  <span key={i} className={`h-1.5 w-5 rounded-full ${i < done ? 'bg-green-500' : 'bg-slate-200'}`} />
                ))}
              </span>
              <span className="text-[12px] font-medium tabular-nums text-slate-600">
                {done} of {required} conversations completed
              </span>
              {done >= required && <CheckCircle2 className="h-4 w-4 text-green-600" />}
            </div>
          )}

          <button onClick={onStartTesting}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 py-3 text-[14px] font-bold text-white shadow-lg shadow-orange-500/25 transition hover:bg-orange-600">
            <Sparkles className="h-4 w-4" /> Let&rsquo;s Test My Campaign
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
